import {useCallback, useState} from 'react';

import CryptoJS from 'crypto-js';

import {ADDITIONAL_DOCUMENTS_EVENTS} from '@/app/auth/events/AdditionalDocumentsEvents';
import {KYC_API_EVENTS} from '@/app/auth/events/KycApiEvents';
import {selectUser, useAppSelector} from '@/app/store';
import {selectTokenData} from '@/app/store/tokenSlice';
import {CKycDocumentTypes} from '@/dataSource/src/api/kyc/constants';
import {uploadFileToS3} from '@/dataSource/src/api/kyc/s3';
import {ErrorManager} from '@/utils/errors/errorManager';
import logError, {logBreadcrumb} from '@/utils/errors/logError';
import {getFirstName} from '@/utils/getFirstName';
import {getLastName} from '@/utils/getLastName';

import {
  usePostDocsSubmittedMutation,
  usePostTokenPlaidIdvMutation,
  usePostTokenPlaidMutation,
  usePutAdditionalDocumentsMutation,
} from './api';
import {
  IKycAdditionalDocumentRequest,
  IKycAdditionalDocumentResponse,
  IKycUploadFileRequest,
  IPostPlaidTokenRequest,
  IPutAdditionalDocument,
} from './entities';
import {useTrackers} from './trackers';

export type AddressFormKyc = {
  addressLineShortened: string;
  addressLine1: string;
  addressLine2: string;
  city: string;
  state: string;
  zipCode: string;
  region: string;
};

async function calculateMD5(file: File): Promise<string> {
  const arrayBuffer = await readFileAsArrayBuffer(file);
  const wordArray = CryptoJS.lib.WordArray.create(arrayBuffer);
  return CryptoJS.MD5(wordArray).toString(CryptoJS.enc.Base64);
}

const fileName = 'useRepository Kyc';

function readFileAsArrayBuffer(file: File): Promise<ArrayBuffer> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = event => resolve(event.target?.result as ArrayBuffer);
    reader.onerror = error => reject(error);
    reader.readAsArrayBuffer(file);
  });
}

export const useKycRepository = () => {
  const [triggerPostTokenPlaid] = usePostTokenPlaidMutation();
  const [triggerPostTokenIdvPlaid] = usePostTokenPlaidIdvMutation();
  const [triggerPostDocsSubmitted] = usePostDocsSubmittedMutation();
  const [triggerPutAdditionalDocuments] = usePutAdditionalDocumentsMutation();
  const [plaidToken, setPlaidToken] = useState<string>('');
  const [plaidIdvToken, setPlaidIdvToken] = useState<string>('');
  const [isGetTokenPlaidError, setIsGetTokenPlaidError] =
    useState<boolean>(false);
  const userData = useAppSelector(selectUser);
  const authData = useAppSelector(selectTokenData);
  const {trackIDVEvents} = useTrackers();

  const handleGetTokenPlaid = useCallback(
    async (formAddress: AddressFormKyc) => {
      setIsGetTokenPlaidError(false);
      const tokenRequest: IPostPlaidTokenRequest = {
        user_id: userData?.id || '',
        given_name: getFirstName(authData?.name),
        family_name: getLastName(authData?.name),
        phone_number: userData?.phone,
        email_address: userData?.email,
        country: 'US',
        city: formAddress.city,
        street: formAddress.addressLineShortened,
        postal_code: formAddress.zipCode,
        street2: formAddress.addressLine2,
        region: formAddress.region,
        onboarding_version: userData?.onboarding_version,
      };

      try {
        logBreadcrumb({
          category: `KYC.useRepository.handleGetTokenPlaid`,
          type: 'query',
          message: 'Getting Plaid token',
          data: {
            tokenRequest,
          },
        });
        const resultToken = await triggerPostTokenPlaid(tokenRequest).unwrap();
        logBreadcrumb({
          category: `KYC.useRepository.handleGetTokenPlaid`,
          type: 'query',
          message: 'Get Plaid token success',
          data: {
            resultToken,
          },
        });
        setPlaidToken(resultToken?.token);
      } catch (error) {
        ErrorManager.handleError(error);
        logError(error, {
          customTitle: 'KYC - Error getting Plaid token',
          fileName: fileName,
          functionName: 'handleGetTokenPlaid',
        });
        setIsGetTokenPlaidError(true);
      }
    },
    [triggerPostTokenPlaid, userData, authData],
  );

  const handleGetTokenIdvPlaid = useCallback(async () => {
    try {
      logBreadcrumb({
        category: `KYC.useRepository.handleGetTokenIdvPlaid`,
        type: 'query',
        message: 'Get IDV Plaid token success',
      });
      const resultToken = await triggerPostTokenIdvPlaid().unwrap();
      logBreadcrumb({
        category: `KYC.useRepository.handleGetTokenIdvPlaid`,
        type: 'query',
        message: 'Get IDV Plaid token success',
        data: {
          resultToken,
        },
      });
      setPlaidIdvToken(resultToken?.token);
      trackIDVEvents(KYC_API_EVENTS.API_KYC_IDV_CREATE_TOKEN_SUCCESS);
    } catch (error) {
      ErrorManager.handleError(error);
      trackIDVEvents(KYC_API_EVENTS.API_KYC_IDV_CREATE_TOKEN_ERROR, error);
      logError(error, {
        customTitle: 'KYC - Error getting IDV Plaid token',
        fileName: fileName,
        functionName: 'handleGetTokenIdvPlaid',
      });
    }
  }, [trackIDVEvents, triggerPostTokenIdvPlaid]);

  const handlePostDocsSubmitted = useCallback(async () => {
    try {
      logBreadcrumb({
        category: `KYC.useRepository.handlePostDocsSubmitted`,
        type: 'query',
        message: 'Submitting documents',
      });
      await triggerPostDocsSubmitted().unwrap();
      trackIDVEvents(
        KYC_API_EVENTS.API_KYC_CHANGE_STATE_FOR_UPLOAD_DOCUMENTS_SUCCESS,
      );
    } catch (error) {
      ErrorManager.handleError(error);
      trackIDVEvents(
        KYC_API_EVENTS.API_KYC_CHANGE_STATE_FOR_UPLOAD_DOCUMENTS_ERROR,
        error,
      );
      logError(error, {
        customTitle: 'KYC - Error submitting documents',
        fileName: fileName,
        functionName: 'handlePostDocsSubmitted',
      });
    }
  }, [trackIDVEvents, triggerPostDocsSubmitted]);

  const handleCreateSignedUrl = useCallback(
    async (documentMetadata: IKycAdditionalDocumentRequest) => {
      try {
        logBreadcrumb({
          category: `KYC.useRepository.handleCreateSignedUrl`,
          type: 'query',
          message: 'Creating signed URL for additional document',
          data: {
            documentMetadata,
          },
        });
        const result: IKycAdditionalDocumentResponse =
          await triggerPutAdditionalDocuments(documentMetadata).unwrap();
        logBreadcrumb({
          category: `KYC.useRepository.handleCreateSignedUrl`,
          type: 'query',
          message: 'Created signed URL for additional document',
          data: {
            result,
          },
        });
        return result;
      } catch (error) {
        logError(error, {
          customTitle:
            'KYC - Error creating signed url for additional document',
          fileName: fileName,
          functionName: 'handleCreateSignedUrl',
          data: {documentMetadata},
        });
        throw error;
      }
    },
    [triggerPutAdditionalDocuments],
  );

  const handleUploadFileToS3 = useCallback(
    async (
      documentMetadata: IKycAdditionalDocumentRequest,
      file: File,
      signedUrl: string,
      abortSignal: AbortSignal,
    ) => {
      try {
        const bodyRequestS3: IKycUploadFileRequest = {
          document: documentMetadata,
          file: file,
          userId: userData?.id || '',
        };
        logBreadcrumb({
          category: `KYC.useRepository.handleUploadFileToS3`,
          type: 'query',
          message: 'Uploading file to S3',
          data: {
            bodyRequestS3,
          },
        });
        await uploadFileToS3(bodyRequestS3, signedUrl, abortSignal);
      } catch (error) {
        logError(error, {
          customTitle: 'KYC - Repository - Error uploading file to S3',
          fileName: fileName,
          functionName: 'handleUploadFileToS3',
          data: {
            document: documentMetadata,
            file: {
              name: file.name,
              size: file.size,
              type: file.type,
            },
          },
        });
        throw error;
      }
    },
    [userData?.id],
  );

  const handlePutAdditionalDocuments = useCallback(
    async (document: IPutAdditionalDocument) => {
      try {
        const hash = await calculateMD5(document.file);

        const documentMetadata: IKycAdditionalDocumentRequest = {
          document_type: document.documentType,
          content_type: document.file.type,
          content_md5: hash,
        };
        logBreadcrumb({
          category: `KYC.useRepository.handlePutAdditionalDocuments`,
          type: 'query',
          message: 'handleCreateSignedUrl',
          data: {
            documentMetadata,
          },
        });
        const result = await handleCreateSignedUrl(documentMetadata);
        logBreadcrumb({
          category: `KYC.useRepository.handlePutAdditionalDocuments`,
          type: 'query',
          message: 'result handleCreateSignedUrl',
          data: {
            result,
          },
        });
        await handleUploadFileToS3(
          documentMetadata,
          document.file,
          result.signed_url,
          document.signal,
        );
      } catch (error) {
        const eventName =
          document.documentType === CKycDocumentTypes.DocumentUtilityBill
            ? ADDITIONAL_DOCUMENTS_EVENTS.API_ERROR_KYC_ADDITIONAL_DOCUMENTS_UTILITY_BILL
            : ADDITIONAL_DOCUMENTS_EVENTS.API_ERROR_KYC_ADDITIONAL_DOCUMENTS_PHONE_BILL;
        trackIDVEvents(eventName, error);
        logError(error, {
          customTitle: 'KYC - Error uploading additional document',
          fileName: fileName,
          functionName: 'handlePutAdditionalDocuments',
          data: {
            documentType: document.documentType,
            file: {
              name: document.file.name,
              size: document.file.size,
              type: document.file.type,
            },
          },
        });
        throw error;
      }
    },
    [handleCreateSignedUrl, handleUploadFileToS3, trackIDVEvents],
  );

  return {
    plaidToken,
    plaidIdvToken,
    isGetTokenPlaidError,
    handleGetTokenPlaid,
    handleGetTokenIdvPlaid,
    handlePostDocsSubmitted,
    handlePutAdditionalDocuments,
  };
};
